import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { apiPost } from '@/lib/api/client';
import type { PresentationData } from '@/types/presentation';

interface GenerationOptions {
  topic: string;
  slideCount: number;
  style?: 'professional' | 'casual' | 'creative' | 'minimalist';
  targetAudience?: string;
  includeImages?: boolean;
  additionalContext?: string;
  temperature?: number;
  maxTokens?: number;
}

interface GenerationState {
  isGenerating: boolean;
  isGeneratingOutline: boolean;
  isRegeneratingSlide: boolean;
  error: string | null;
  progress: number;
  currentStage: string;
}

interface UsePresentationGeneratorReturn {
  state: GenerationState;
  generatePresentation: (options: GenerationOptions) => Promise<PresentationData | null>;
  generateOutline: (topic: string, slideCount: number, targetAudience?: string) => Promise<string | null>;
  regenerateSlide: (
    presentationContext: string,
    slideNumber: number,
    slideTitle: string,
    feedback?: string
  ) => Promise<Record<string, unknown> | null>;
  resetError: () => void;
}

export const usePresentationGenerator = (): UsePresentationGeneratorReturn => {
  const { toast } = useToast();
  const [state, setState] = useState<GenerationState>({
    isGenerating: false,
    isGeneratingOutline: false,
    isRegeneratingSlide: false,
    error: null,
    progress: 0,
    currentStage: '',
  });

  const resetError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const generatePresentation = useCallback(
    async (options: GenerationOptions): Promise<PresentationData | null> => {
      setState((prev) => ({
        ...prev,
        isGenerating: true,
        error: null,
        progress: 0,
        currentStage: 'Initializing AI generation...',
      }));

      try {
        // Simulate progress updates
        const progressStages = [
          'Analyzing topic...',
          'Generating outline...',
          'Creating slides...',
          'Adding visual elements...',
          'Finalizing presentation...',
        ];

        let progressIndex = 0;
        const progressInterval = setInterval(() => {
          if (progressIndex < progressStages.length) {
            setState((prev) => ({
              ...prev,
              progress: Math.min(90, ((progressIndex + 1) / progressStages.length) * 100),
              currentStage: progressStages[progressIndex],
            }));
            progressIndex++;
          }
        }, 1000);

        const response = await apiPost<{
          success: boolean;
          presentation: PresentationData;
        }>('/presentations/generate', options);

        clearInterval(progressInterval);

        if (response && response.success && response.presentation) {
          setState((prev) => ({
            ...prev,
            isGenerating: false,
            progress: 100,
            currentStage: 'Complete!',
          }));

          toast({
            title: 'Presentation Generated',
            description: `Successfully created "${response.presentation.metadata.title}" with ${response.presentation.slides.length} slides`,
          });

          return response.presentation;
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error: unknown) {
        console.error('[usePresentationGenerator] Generation error:', error);
        const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
          || (error as { message?: string })?.message
          || 'Failed to generate presentation';

        setState((prev) => ({
          ...prev,
          isGenerating: false,
          error: errorMessage,
          progress: 0,
          currentStage: '',
        }));

        toast({
          title: 'Generation Failed',
          description: errorMessage,
          variant: 'destructive',
        });

        return null;
      }
    },
    [toast]
  );

  const generateOutline = useCallback(
    async (topic: string, slideCount: number, targetAudience?: string): Promise<string | null> => {
      setState((prev) => ({
        ...prev,
        isGeneratingOutline: true,
        error: null,
      }));

      try {
        const response = await apiPost<{ success: boolean; outline: string }>('/presentations/generate/outline', {
          topic,
          slideCount,
          targetAudience,
        });

        if (response && response.success && response.outline) {
          setState((prev) => ({
            ...prev,
            isGeneratingOutline: false,
          }));

          return response.outline;
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error: unknown) {
        console.error('[usePresentationGenerator] Outline generation error:', error);
        const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
          || (error as { message?: string })?.message
          || 'Failed to generate outline';

        setState((prev) => ({
          ...prev,
          isGeneratingOutline: false,
          error: errorMessage,
        }));

        toast({
          title: 'Outline Generation Failed',
          description: errorMessage,
          variant: 'destructive',
        });

        return null;
      }
    },
    [toast]
  );

  const regenerateSlide = useCallback(
    async (
      presentationContext: string,
      slideNumber: number,
      slideTitle: string,
      feedback?: string
    ): Promise<Record<string, unknown> | null> => {
      setState((prev) => ({
        ...prev,
        isRegeneratingSlide: true,
        error: null,
      }));

      try {
        const response = await apiPost<{ success: boolean; slide: Record<string, unknown> }>(
          `/presentations/generate/slide/${slideNumber}`,
          {
            presentationContext,
            slideTitle,
            feedback,
          }
        );

        if (response && response.success && response.slide) {
          setState((prev) => ({
            ...prev,
            isRegeneratingSlide: false,
          }));

          toast({
            title: 'Slide Regenerated',
            description: `Slide ${slideNumber} has been regenerated`,
          });

          return response.slide;
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error: unknown) {
        console.error('[usePresentationGenerator] Slide regeneration error:', error);
        const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
          || (error as { message?: string })?.message
          || 'Failed to regenerate slide';

        setState((prev) => ({
          ...prev,
          isRegeneratingSlide: false,
          error: errorMessage,
        }));

        toast({
          title: 'Slide Regeneration Failed',
          description: errorMessage,
          variant: 'destructive',
        });

        return null;
      }
    },
    [toast]
  );

  return {
    state,
    generatePresentation,
    generateOutline,
    regenerateSlide,
    resetError,
  };
};
